import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { signOut } from '../../lib/auth';
import { registerPushNotifications } from '../../lib/notifications';
import './Settings.css';

export default function Settings() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);

  // Editable fields
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  // Notification state
  const [notifPermission, setNotifPermission] = useState('default');
  const [logReminderTime, setLogReminderTime] = useState('20:00');

  useEffect(() => {
    if (user) loadSettings();
    checkNotifPermission();
  }, [user]);

  async function loadSettings() {
    setLoading(true);
    const { data } = await supabase
      .from('clients')
      .select('*')
      .eq('auth_user_id', user.id)
      .single();

    if (data) {
      setClient(data);
      setName(data.name || '');
      setEmail(data.email || '');
    }
    setLoading(false);
  }

  function checkNotifPermission() {
    if ('Notification' in window) {
      setNotifPermission(Notification.permission);
    }
  }

  async function handleSaveProfile(e) {
    e.preventDefault();
    setSaving(true);
    setMessage('');

    const { error } = await supabase
      .from('clients')
      .update({ name, email })
      .eq('id', client.id);

    if (error) {
      setMessage('Failed to save. Please try again.');
    } else {
      setMessage('Profile updated!');
      setTimeout(() => setMessage(''), 3000);
    }
    setSaving(false);
  }

  async function handleEnableNotifications() {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission();
      setNotifPermission(permission);

      if (permission === 'granted' && user) {
        await registerPushNotifications(user.id);
      }
    }
  }

  async function handleLogout() {
    await signOut();
    navigate('/login', { replace: true });
  }

  if (loading) {
    return (
      <div className="screen container">
        <div style={{ textAlign: 'center', padding: '80px 0', color: 'var(--color-text-muted)' }}>
          Loading...
        </div>
      </div>
    );
  }

  return (
    <div className="screen container settings-screen">
      <h1 className="settings-title">Settings</h1>

      {/* Profile section */}
      <div className="settings-section">
        <h3 className="settings-section-title">Profile</h3>
        <form className="settings-form" onSubmit={handleSaveProfile}>
          <div className="form-group">
            <label htmlFor="settings-name">Name</label>
            <input
              id="settings-name"
              type="text"
              className="form-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="settings-email">Email</label>
            <input
              id="settings-email"
              type="email"
              className="form-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          {message && (
            <p className={`settings-msg ${message.includes('Failed') ? 'settings-msg-error' : 'settings-msg-ok'}`}>
              {message}
            </p>
          )}
          <button type="submit" className="btn btn-primary" style={{ width: '100%' }} disabled={saving}>
            {saving ? 'Saving...' : 'Save Profile'}
          </button>
        </form>
      </div>

      {/* Notification section */}
      <div className="settings-section">
        <h3 className="settings-section-title">Notifications</h3>

        <div className="settings-notif-row">
          <div>
            <p className="settings-notif-label">Push Notifications</p>
            <p className="settings-notif-desc">
              {notifPermission === 'granted'
                ? 'Enabled — you\'ll receive reminders for supplements, tasks, and daily logging.'
                : notifPermission === 'denied'
                ? 'Blocked — enable notifications in your browser/device settings.'
                : 'Get gentle reminders for supplements, tasks, and daily logging.'}
            </p>
          </div>
          {notifPermission !== 'granted' && notifPermission !== 'denied' && (
            <button className="btn btn-secondary" onClick={handleEnableNotifications}>
              Enable
            </button>
          )}
          {notifPermission === 'granted' && (
            <span className="settings-notif-badge">On</span>
          )}
        </div>

        <div className="settings-notif-row">
          <div>
            <p className="settings-notif-label">Daily Log Reminder</p>
            <p className="settings-notif-desc">
              We'll remind you to log how you're feeling each day.
            </p>
          </div>
          <input
            type="time"
            className="form-input settings-time-input"
            value={logReminderTime}
            onChange={(e) => setLogReminderTime(e.target.value)}
          />
        </div>

        <div className="settings-notif-row">
          <div>
            <p className="settings-notif-label">Quiet Hours</p>
            <p className="settings-notif-desc">
              No notifications between 10pm and 7am, unless a task is specifically scheduled in that window.
            </p>
          </div>
        </div>
      </div>

      {/* Account section */}
      <div className="settings-section">
        <h3 className="settings-section-title">Account</h3>
        <button className="settings-logout-btn" onClick={handleLogout}>
          Sign Out
        </button>
      </div>
    </div>
  );
}
